import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import TopNav from "@/components/TopNav";
import { Sparkles, Loader2, Plus, Trash2, ChevronDown, ChevronRight, BookOpen, Users, BarChart3, Pencil, Eye, ArrowUp, ArrowDown, Save, X, FileSpreadsheet } from "lucide-react";
import ImportWizard from "@/components/ImportWizard";

const COLOR_TOKENS = {
  amber: "text-amber-600",
  blue:  "text-blue-700",
  red:   "text-red-600",
  stone: "text-stone-600",
};

/* ---- helpers ---- */
function normalizeYoutube(url) {
  if (!url) return "";
  // Already embed
  if (url.includes("/embed/")) return url;
  // youtu.be/XXX
  const short = url.match(/youtu\.be\/([\w-]+)/);
  if (short) return `https://www.youtube.com/embed/${short[1]}`;
  // youtube.com/watch?v=XXX
  const watch = url.match(/[?&]v=([\w-]+)/);
  if (watch) return `https://www.youtube.com/embed/${watch[1]}`;
  // youtube.com/shorts/XXX
  const shorts = url.match(/\/shorts\/([\w-]+)/);
  if (shorts) return `https://www.youtube.com/embed/${shorts[1]}`;
  return url;
}

function Stat({label, value, testid, icon: Icon}) {
  return (
    <div className="tw-card p-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="tw-mono-label">{label}</div>
        {Icon && <Icon size={14} className="text-stone-400"/>}
      </div>
      <div className="tw-display text-3xl font-extrabold mt-2">{value}</div>
    </div>
  );
}

function TabBtn({active, onClick, children, testid}) {
  return (
    <button onClick={onClick} className={`pb-3 px-1 text-sm font-semibold border-b-2 transition-colors ${active?"border-stone-900 text-stone-900":"border-transparent text-stone-500 hover:text-stone-800"}`} data-testid={testid}>{children}</button>
  );
}

function IconBtn({onClick, title, children, color="stone", testid, disabled}) {
  return (
    <button onClick={onClick} disabled={disabled} title={title} data-testid={testid}
      className={`p-1.5 rounded-md hover:bg-stone-100 disabled:opacity-30 disabled:cursor-not-allowed ${COLOR_TOKENS[color]}`}>
      {children}
    </button>
  );
}

/* ---------- Overview tab ---------- */
function Overview() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  useEffect(() => { api.get("/admin/stats").then(r=>setStats(r.data)); api.get("/admin/users").then(r=>setUsers(r.data)); }, []);
  return (
    <>
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 stagger">
          <Stat label="Users" value={stats.users_total} testid="admin-stat-users" icon={Users}/>
          <Stat label="Learners" value={stats.learners} testid="admin-stat-learners"/>
          <Stat label="Courses" value={stats.courses} testid="admin-stat-courses" icon={BookOpen}/>
          <Stat label="Blocks" value={stats.blocks} testid="admin-stat-blocks"/>
          <Stat label="Lessons" value={stats.lessons} testid="admin-stat-lessons"/>
          <Stat label="Attempts" value={stats.attempts} testid="admin-stat-attempts" icon={BarChart3}/>
        </div>
      )}
      <section className="mt-10">
        <div className="tw-mono-label" data-testid="admin-users-label">USERS</div>
        <div className="tw-card mt-3 overflow-hidden">
          <table className="w-full text-sm" data-testid="admin-users-table">
            <thead className="bg-stone-50 text-left tw-mono-label">
              <tr><th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">Role</th><th className="p-3">Auth</th></tr>
            </thead>
            <tbody>
              {users.map(u=>(
                <tr key={u.user_id} className="border-t border-stone-100">
                  <td className="p-3 font-medium">{u.name}</td>
                  <td className="p-3 text-stone-600">{u.email}</td>
                  <td className="p-3"><span className={`tw-pill ${u.role==="admin"?"tw-pill-prog":"tw-pill-done"}`}>{u.role}</span></td>
                  <td className="p-3 text-stone-500">{u.auth_method}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}

/* ---------- New Course form ---------- */
function NewCourseForm({ onCreated, onCancel }) {
  const [form, setForm] = useState({code:"", title:"", subtitle:"", description:"", category:"Management", author:"", duration_hours:1, cover_color:"#185FA5", published:true});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      await api.post("/admin/courses", form);
      onCreated();
    } catch (e) { setErr(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="tw-card p-6 grid sm:grid-cols-2 gap-4" data-testid="new-course-form">
      <div><label className="tw-mono-label block mb-1">Code</label><input className="tw-input" placeholder="TA0003" value={form.code} onChange={e=>setForm({...form,code:e.target.value.toUpperCase()})} required data-testid="new-course-code"/></div>
      <div><label className="tw-mono-label block mb-1">Title</label><input className="tw-input" value={form.title} onChange={e=>setForm({...form,title:e.target.value})} required data-testid="new-course-title"/></div>
      <div className="sm:col-span-2"><label className="tw-mono-label block mb-1">Subtitle</label><input className="tw-input" value={form.subtitle} onChange={e=>setForm({...form,subtitle:e.target.value})}/></div>
      <div className="sm:col-span-2"><label className="tw-mono-label block mb-1">Description</label><textarea className="tw-input" rows={3} value={form.description} onChange={e=>setForm({...form,description:e.target.value})}/></div>
      <div><label className="tw-mono-label block mb-1">Category</label><input className="tw-input" value={form.category} onChange={e=>setForm({...form,category:e.target.value})}/></div>
      <div><label className="tw-mono-label block mb-1">Author</label><input className="tw-input" value={form.author} onChange={e=>setForm({...form,author:e.target.value})}/></div>
      <div><label className="tw-mono-label block mb-1">Duration (hours)</label><input className="tw-input" type="number" step="0.1" value={form.duration_hours} onChange={e=>setForm({...form,duration_hours:parseFloat(e.target.value)||1})}/></div>
      <div><label className="tw-mono-label block mb-1">Cover color</label><input className="tw-input" type="color" value={form.cover_color} onChange={e=>setForm({...form,cover_color:e.target.value})}/></div>
      <div className="sm:col-span-2 flex items-center justify-between flex-wrap gap-2">
        <label className="text-sm flex items-center gap-2"><input type="checkbox" checked={form.published} onChange={e=>setForm({...form,published:e.target.checked})}/> Published</label>
        {err && <span className="text-red-600 text-sm">{err}</span>}
        <div className="flex gap-2">
          <button type="button" onClick={onCancel} className="text-sm text-stone-500 px-3">Cancel</button>
          <button disabled={busy} className="tw-btn-primary inline-flex items-center gap-2" data-testid="new-course-submit">{busy ? <Loader2 size={14} className="animate-spin"/> : <Plus size={14}/>} Create course</button>
        </div>
      </div>
    </form>
  );
}

/* ---------- Course Info Editor (edit existing course) ---------- */
function CourseInfoEditor({ course, onSaved, onCancel }) {
  const [form, setForm] = useState({...course});
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    const {title, subtitle, description, category, author, duration_hours, cover_color, published} = form;
    try {
      await api.patch(`/admin/courses/${course.course_id}`, {title, subtitle, description, category, author, duration_hours: parseFloat(duration_hours)||1, cover_color, published});
      onSaved();
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="bg-stone-50 border border-stone-200 rounded-xl p-5 grid sm:grid-cols-2 gap-3 mt-3" data-testid={`edit-course-form-${course.course_id}`}>
      <div className="sm:col-span-2"><label className="tw-mono-label block mb-1">Title</label><input className="tw-input" value={form.title||""} onChange={e=>setForm({...form,title:e.target.value})} required data-testid="edit-course-title"/></div>
      <div className="sm:col-span-2"><label className="tw-mono-label block mb-1">Subtitle</label><input className="tw-input" value={form.subtitle||""} onChange={e=>setForm({...form,subtitle:e.target.value})}/></div>
      <div className="sm:col-span-2"><label className="tw-mono-label block mb-1">Description</label><textarea className="tw-input" rows={3} value={form.description||""} onChange={e=>setForm({...form,description:e.target.value})}/></div>
      <div><label className="tw-mono-label block mb-1">Category</label><input className="tw-input" value={form.category||""} onChange={e=>setForm({...form,category:e.target.value})}/></div>
      <div><label className="tw-mono-label block mb-1">Author</label><input className="tw-input" value={form.author||""} onChange={e=>setForm({...form,author:e.target.value})}/></div>
      <div><label className="tw-mono-label block mb-1">Duration (hours)</label><input className="tw-input" type="number" step="0.1" value={form.duration_hours||1} onChange={e=>setForm({...form,duration_hours:e.target.value})}/></div>
      <div><label className="tw-mono-label block mb-1">Cover color</label><input className="tw-input" type="color" value={form.cover_color||"#185FA5"} onChange={e=>setForm({...form,cover_color:e.target.value})}/></div>
      <div className="sm:col-span-2 flex items-center justify-between gap-2">
        <label className="text-sm flex items-center gap-2"><input type="checkbox" checked={!!form.published} onChange={e=>setForm({...form,published:e.target.checked})}/> Published (visible in catalog)</label>
        <div className="flex gap-2">
          <button type="button" onClick={onCancel} className="text-sm text-stone-500 px-3">Cancel</button>
          <button disabled={busy} className="tw-btn-primary inline-flex items-center gap-2 text-sm" data-testid="edit-course-save"><Save size={14}/> Save</button>
        </div>
      </div>
    </form>
  );
}

/* ---------- Quiz Editor (per lesson) ---------- */
function QuizEditor({ lessonId, onClose }) {
  const [questions, setQuestions] = useState([]);
  const [reading, setReading] = useState("");
  const [busy, setBusy] = useState(false);
  const [savedMsg, setSavedMsg] = useState("");

  useEffect(() => {
    api.get(`/lessons/${lessonId}`).then(r => {
      const q = r.data.quiz?.questions || [];
      setQuestions(q.length ? q : [{q:"", options:["","","",""], correct:0, explanation:""}]);
      setReading(r.data.lesson?.reading || "");
    });
  }, [lessonId]);

  const updateQ = (i, patch) => setQuestions(qs => qs.map((q,idx) => idx===i ? {...q, ...patch} : q));
  const updateOpt = (i, j, val) => setQuestions(qs => qs.map((q,idx)=> idx===i ? {...q, options: q.options.map((o,jj)=>jj===j?val:o)} : q));
  const addQ = () => setQuestions(qs => [...qs, {q:"", options:["","","",""], correct:0, explanation:""}]);
  const removeQ = (i) => setQuestions(qs => qs.filter((_,idx)=>idx!==i));

  const aiGen = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/ai/generate-quiz", { reading_text: reading, num_questions: 4 });
      setQuestions(data.questions || []);
    } catch (e) { alert("AI generation failed"); }
    finally { setBusy(false); }
  };

  const save = async () => {
    await api.post("/admin/quiz", { lesson_id: lessonId, questions });
    setSavedMsg("Saved"); setTimeout(()=>setSavedMsg(""), 1500);
  };

  return (
    <div className="bg-stone-50 border border-stone-200 rounded-xl p-5 mt-3" data-testid="quiz-editor">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="tw-mono-label">QUIZ EDITOR</div>
        <div className="flex items-center gap-2">
          {savedMsg && <span className="text-emerald-600 text-sm">{savedMsg}</span>}
          <button onClick={aiGen} disabled={busy || !reading.trim()} className="tw-btn-outline text-sm inline-flex items-center gap-2" data-testid="quiz-ai-gen-btn">
            {busy ? <Loader2 size={14} className="animate-spin"/> : <Sparkles size={14}/>} Generate with AI
          </button>
        </div>
      </div>
      <div className="mt-4 space-y-4">
        {questions.map((q, i) => (
          <div key={i} className="bg-white border border-stone-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <span className="tw-mono-label">Q{i+1}</span>
              <IconBtn onClick={()=>removeQ(i)} color="red" title="Remove question"><Trash2 size={14}/></IconBtn>
            </div>
            <input className="tw-input mt-2" placeholder="Question" value={q.q} onChange={e=>updateQ(i,{q:e.target.value})}/>
            <div className="grid sm:grid-cols-2 gap-2 mt-2">
              {q.options.map((opt, j) => (
                <label key={j} className="flex items-center gap-2 text-sm">
                  <input type="radio" name={`correct-${i}`} checked={q.correct===j} onChange={()=>updateQ(i,{correct:j})}/>
                  <input className="tw-input flex-1" placeholder={`Option ${j+1}`} value={opt} onChange={e=>updateOpt(i,j,e.target.value)}/>
                </label>
              ))}
            </div>
            <input className="tw-input mt-2" placeholder="Explanation (optional)" value={q.explanation} onChange={e=>updateQ(i,{explanation:e.target.value})}/>
          </div>
        ))}
      </div>
      <div className="mt-4 flex items-center gap-2 flex-wrap">
        <button onClick={addQ} className="tw-btn-outline text-sm inline-flex items-center gap-1"><Plus size={14}/> Add question</button>
        <div className="flex-1"/>
        <button onClick={onClose} className="text-sm text-stone-500 px-3">Close</button>
        <button onClick={save} className="tw-btn-primary text-sm inline-flex items-center gap-1" data-testid="quiz-save-btn"><Save size={14}/> Save quiz</button>
      </div>
    </div>
  );
}

/* ---------- Lesson Editor (create + edit) ---------- */
function LessonForm({ blockId, initial, onSaved, onCancel }) {
  const isEdit = !!initial;
  const [form, setForm] = useState(initial ? {
    title: initial.title || "",
    objectives: (initial.objectives || []).join("\n"),
    video_url: initial.video_url || "",
    slides_url: initial.slides_url || "",
    reading: initial.reading || "",
    interactive: initial.interactive || "",
    order: initial.order || 1,
  } : { title:"", objectives:"", video_url:"", slides_url:"", reading:"", interactive:"", order:1 });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    const payload = {
      title: form.title,
      objectives: form.objectives.split("\n").map(s=>s.trim()).filter(Boolean),
      video_url: normalizeYoutube(form.video_url.trim()),
      slides_url: form.slides_url.trim(),
      reading: form.reading,
      interactive: form.interactive,
      order: parseInt(form.order) || 1,
    };
    try {
      if (isEdit) {
        await api.patch(`/admin/lessons/${initial.lesson_id}`, payload);
      } else {
        await api.post("/admin/lessons", {...payload, block_id: blockId});
      }
      onSaved();
    } finally { setBusy(false); }
  };

  const normalizedPreview = normalizeYoutube(form.video_url.trim());
  const videoChanged = normalizedPreview && normalizedPreview !== form.video_url.trim();

  return (
    <form onSubmit={submit} className="bg-stone-50 border border-stone-200 rounded-xl p-4 mt-2 space-y-2" data-testid={isEdit ? `edit-lesson-form-${initial.lesson_id}` : `new-lesson-form-${blockId}`}>
      <div className="tw-mono-label">{isEdit ? "EDIT LESSON" : "NEW LESSON"}</div>
      <input className="tw-input" placeholder="Lesson title" required value={form.title} onChange={e=>setForm({...form,title:e.target.value})} data-testid="lesson-title-input"/>
      <textarea className="tw-input" rows={3} placeholder="Learning objectives (one per line)" value={form.objectives} onChange={e=>setForm({...form,objectives:e.target.value})} data-testid="lesson-objectives-input"/>
      <div className="grid sm:grid-cols-2 gap-2">
        <div>
          <input className="tw-input" placeholder="Video URL (YouTube link)" value={form.video_url} onChange={e=>setForm({...form,video_url:e.target.value})} data-testid="lesson-video-input"/>
          {videoChanged && <div className="text-[11px] text-stone-500 mt-1">Auto-formatted to: {normalizedPreview}</div>}
        </div>
        <input className="tw-input" placeholder="Slides URL (optional)" value={form.slides_url} onChange={e=>setForm({...form,slides_url:e.target.value})}/>
      </div>
      <textarea className="tw-input" rows={6} placeholder="Reading content (markdown OK — paragraphs separated by blank lines)" value={form.reading} onChange={e=>setForm({...form,reading:e.target.value})} data-testid="lesson-reading-input"/>
      <textarea className="tw-input" rows={2} placeholder="Interactive / reflection prompt" value={form.interactive} onChange={e=>setForm({...form,interactive:e.target.value})} data-testid="lesson-interactive-input"/>
      <div className="flex items-center gap-2 flex-wrap">
        <label className="text-sm text-stone-600 flex items-center gap-2">Order <input className="tw-input w-20" type="number" value={form.order} onChange={e=>setForm({...form,order:e.target.value})}/></label>
        <div className="flex-1"/>
        <button type="button" onClick={onCancel} className="text-sm text-stone-500 px-3">Cancel</button>
        <button disabled={busy} className="tw-btn-primary text-sm inline-flex items-center gap-1" data-testid="lesson-save-btn"><Save size={14}/> {busy?"Saving…":(isEdit?"Save changes":"Add lesson")}</button>
      </div>
    </form>
  );
}

/* ---------- Block Editor (create + edit) ---------- */
function BlockForm({ courseId, initial, onSaved, onCancel }) {
  const isEdit = !!initial;
  const [form, setForm] = useState(initial ? {
    title: initial.title || "", summary: initial.summary || "", learning_outcome: initial.learning_outcome || "", order: initial.order || 1
  } : {title:"", summary:"", learning_outcome:"", order:1});
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    const payload = {title: form.title, summary: form.summary, learning_outcome: form.learning_outcome, order: parseInt(form.order)||1};
    try {
      if (isEdit) await api.patch(`/admin/blocks/${initial.block_id}`, payload);
      else await api.post("/admin/blocks", {...payload, course_id: courseId});
      onSaved();
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="bg-stone-50 border border-stone-200 rounded-xl p-4 mt-2 space-y-2" data-testid={isEdit?`edit-block-form-${initial.block_id}`:`new-block-form-${courseId}`}>
      <div className="tw-mono-label">{isEdit?"EDIT BLOCK":"NEW BLOCK"}</div>
      <input className="tw-input" placeholder="Block title" required value={form.title} onChange={e=>setForm({...form,title:e.target.value})}/>
      <input className="tw-input" placeholder="Summary" value={form.summary} onChange={e=>setForm({...form,summary:e.target.value})}/>
      <textarea className="tw-input" rows={2} placeholder="Learning outcome" value={form.learning_outcome} onChange={e=>setForm({...form,learning_outcome:e.target.value})}/>
      <div className="flex items-center gap-2 flex-wrap">
        <label className="text-sm text-stone-600 flex items-center gap-2">Order <input className="tw-input w-20" type="number" value={form.order} onChange={e=>setForm({...form,order:e.target.value})}/></label>
        <div className="flex-1"/>
        <button type="button" onClick={onCancel} className="text-sm text-stone-500 px-3">Cancel</button>
        <button disabled={busy} className="tw-btn-primary text-sm inline-flex items-center gap-1"><Save size={14}/> {busy?"Saving…":(isEdit?"Save changes":"Add block")}</button>
      </div>
    </form>
  );
}

/* ---------- Course Editor ---------- */
function CourseEditor({ courseId, refreshCourses }) {
  const [data, setData] = useState(null);
  const [expandedBlock, setExpandedBlock] = useState(null);
  const [editingBlock, setEditingBlock] = useState(null);   // block_id
  const [editingLesson, setEditingLesson] = useState(null); // lesson_id
  const [newLessonFor, setNewLessonFor] = useState(null);   // block_id
  const [newBlock, setNewBlock] = useState(false);
  const [editingCourse, setEditingCourse] = useState(false);
  const [quizEditingLesson, setQuizEditingLesson] = useState(null);

  const load = () => api.get(`/admin/courses/${courseId}/full`).then(r=>setData(r.data));
  useEffect(() => { load(); }, [courseId]);

  if (!data) return <div className="text-stone-500 p-6">Loading…</div>;

  const lessonsByBlock = {};
  data.lessons.forEach(l => { (lessonsByBlock[l.block_id] = lessonsByBlock[l.block_id] || []).push(l); });
  const sortedBlocks = [...data.blocks].sort((a,b)=>a.order-b.order);

  const delCourse = async () => {
    if (!window.confirm(`Delete ${courseId} and all its blocks & lessons?`)) return;
    await api.delete(`/admin/courses/${courseId}`);
    refreshCourses();
  };
  const delBlock = async (bid) => { if(!window.confirm("Delete this block and all its lessons?")) return; await api.delete(`/admin/blocks/${bid}`); load(); };
  const delLesson = async (lid) => { if(!window.confirm("Delete this lesson and its quiz?")) return; await api.delete(`/admin/lessons/${lid}`); load(); };

  const moveBlock = async (bid, dir) => {
    const idx = sortedBlocks.findIndex(b=>b.block_id===bid);
    const swap = sortedBlocks[idx+dir];
    if (!swap) return;
    const a = sortedBlocks[idx];
    await Promise.all([
      api.patch(`/admin/blocks/${a.block_id}`, {order: swap.order}),
      api.patch(`/admin/blocks/${swap.block_id}`, {order: a.order}),
    ]);
    load();
  };
  const moveLesson = async (block_id, lesson_id, dir) => {
    const sorted = (lessonsByBlock[block_id]||[]).sort((a,b)=>a.order-b.order);
    const idx = sorted.findIndex(l=>l.lesson_id===lesson_id);
    const swap = sorted[idx+dir];
    if (!swap) return;
    const a = sorted[idx];
    await Promise.all([
      api.patch(`/admin/lessons/${a.lesson_id}`, {order: swap.order}),
      api.patch(`/admin/lessons/${swap.lesson_id}`, {order: a.order}),
    ]);
    load();
  };

  const firstLesson = data.lessons[0];
  const previewHref = firstLesson ? `/courses/${courseId}/lessons/${firstLesson.lesson_id}` : `/courses/${courseId}`;

  return (
    <div className="tw-card p-6 mt-3" data-testid={`course-editor-${courseId}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="tw-mono-label">{data.course.course_id} · {data.course.category}</div>
            {!data.course.published && <span className="tw-pill tw-pill-lock">Draft</span>}
          </div>
          <h3 className="tw-display text-2xl font-bold mt-1">{data.course.title}</h3>
          <p className="text-stone-600 text-sm">{data.course.subtitle}</p>
        </div>
        <div className="flex items-center gap-1">
          <a href={previewHref} target="_blank" rel="noreferrer" className="text-sm text-stone-700 hover:text-stone-900 inline-flex items-center gap-1 px-2 py-1.5" data-testid={`preview-course-${courseId}`}><Eye size={14}/> Preview</a>
          <IconBtn onClick={()=>setEditingCourse(s=>!s)} title="Edit course info" testid={`edit-course-btn-${courseId}`} color="blue"><Pencil size={16}/></IconBtn>
          <IconBtn onClick={delCourse} title="Delete course" testid={`delete-course-${courseId}`} color="red"><Trash2 size={16}/></IconBtn>
        </div>
      </div>

      {editingCourse && <CourseInfoEditor course={data.course} onSaved={()=>{setEditingCourse(false); load(); refreshCourses();}} onCancel={()=>setEditingCourse(false)}/>}

      <div className="mt-6">
        <div className="flex items-center justify-between">
          <div className="tw-mono-label">BLOCKS</div>
          {!newBlock && <button onClick={()=>setNewBlock(true)} className="tw-btn-outline text-sm inline-flex items-center gap-1" data-testid={`add-block-${courseId}`}><Plus size={14}/> Add block</button>}
        </div>
        {newBlock && <BlockForm courseId={courseId} onSaved={()=>{setNewBlock(false); load();}} onCancel={()=>setNewBlock(false)}/>}

        <div className="mt-2 space-y-2">
          {sortedBlocks.map((b, bi) => {
            const isOpen = expandedBlock === b.block_id;
            const ls = (lessonsByBlock[b.block_id] || []).sort((a,b)=>a.order-b.order);
            return (
              <div key={b.block_id} className="border border-stone-200 rounded-xl">
                <div className="p-3 flex items-center gap-3 hover:bg-stone-50">
                  <button onClick={()=>setExpandedBlock(isOpen?null:b.block_id)} className="flex items-center gap-2 flex-1 min-w-0 text-left" data-testid={`block-toggle-${b.block_id}`}>
                    {isOpen ? <ChevronDown size={16}/> : <ChevronRight size={16}/>}
                    <div className="font-bold tw-display truncate">Block {b.order} — {b.title}</div>
                  </button>
                  <div className="text-xs text-stone-500 whitespace-nowrap">{ls.length} lesson{ls.length!==1?"s":""}</div>
                  <IconBtn onClick={()=>moveBlock(b.block_id,-1)} disabled={bi===0} title="Move up" testid={`block-up-${b.block_id}`}><ArrowUp size={14}/></IconBtn>
                  <IconBtn onClick={()=>moveBlock(b.block_id, 1)} disabled={bi===sortedBlocks.length-1} title="Move down" testid={`block-down-${b.block_id}`}><ArrowDown size={14}/></IconBtn>
                  <IconBtn onClick={()=>setEditingBlock(editingBlock===b.block_id?null:b.block_id)} title="Edit block" color="blue" testid={`edit-block-${b.block_id}`}><Pencil size={14}/></IconBtn>
                  <IconBtn onClick={()=>delBlock(b.block_id)} title="Delete block" color="red"><Trash2 size={14}/></IconBtn>
                </div>
                {editingBlock === b.block_id && (
                  <div className="px-3 pb-3 border-t border-stone-200">
                    <BlockForm courseId={courseId} initial={b} onSaved={()=>{setEditingBlock(null); load();}} onCancel={()=>setEditingBlock(null)}/>
                  </div>
                )}
                {isOpen && (
                  <div className="border-t border-stone-200 p-3 bg-stone-50/50">
                    {ls.map((l, li) => (
                      <div key={l.lesson_id} className="bg-white border border-stone-200 rounded-lg p-3 mb-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="font-semibold flex-1 min-w-0 truncate">{l.order}. {l.title}</div>
                          <span className="text-[10px] text-stone-400">{l.video_url?"VIDEO ":""}{l.reading?"READING ":""}{l.interactive?"INTERACTIVE ":""}</span>
                          <IconBtn onClick={()=>moveLesson(b.block_id, l.lesson_id, -1)} disabled={li===0} title="Move up" testid={`lesson-up-${l.lesson_id}`}><ArrowUp size={14}/></IconBtn>
                          <IconBtn onClick={()=>moveLesson(b.block_id, l.lesson_id,  1)} disabled={li===ls.length-1} title="Move down" testid={`lesson-down-${l.lesson_id}`}><ArrowDown size={14}/></IconBtn>
                          <a href={`/courses/${courseId}/lessons/${l.lesson_id}`} target="_blank" rel="noreferrer" className="p-1.5 text-stone-600 hover:text-stone-900" title="Preview lesson" data-testid={`preview-lesson-${l.lesson_id}`}><Eye size={14}/></a>
                          <IconBtn onClick={()=>{setEditingLesson(editingLesson===l.lesson_id?null:l.lesson_id); setQuizEditingLesson(null);}} title="Edit lesson content" color="blue" testid={`edit-lesson-${l.lesson_id}`}><Pencil size={14}/></IconBtn>
                          <button onClick={()=>{setQuizEditingLesson(quizEditingLesson===l.lesson_id?null:l.lesson_id); setEditingLesson(null);}} className="text-sm text-blue-700 hover:underline px-2" data-testid={`edit-quiz-${l.lesson_id}`}>
                            {quizEditingLesson===l.lesson_id?"Close":"Quiz"}
                          </button>
                          <IconBtn onClick={()=>delLesson(l.lesson_id)} title="Delete lesson" color="red"><Trash2 size={14}/></IconBtn>
                        </div>
                        {editingLesson === l.lesson_id && <LessonForm blockId={b.block_id} initial={l} onSaved={()=>{setEditingLesson(null); load();}} onCancel={()=>setEditingLesson(null)}/>}
                        {quizEditingLesson === l.lesson_id && <QuizEditor lessonId={l.lesson_id} onClose={()=>{setQuizEditingLesson(null); load();}}/>}
                      </div>
                    ))}
                    {newLessonFor === b.block_id ? (
                      <LessonForm blockId={b.block_id} onSaved={()=>{setNewLessonFor(null); load();}} onCancel={()=>setNewLessonFor(null)}/>
                    ) : (
                      <button onClick={()=>setNewLessonFor(b.block_id)} className="text-sm text-stone-600 inline-flex items-center gap-1 hover:text-stone-900 mt-2" data-testid={`add-lesson-${b.block_id}`}><Plus size={14}/> Add lesson</button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ---------- Courses tab ---------- */
function CoursesTab() {
  const [courses, setCourses] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const load = () => api.get("/courses").then(r=>setCourses(r.data));
  useEffect(() => { load(); }, []);

  return (
    <>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="tw-mono-label">ALL COURSES</div>
        <div className="flex items-center gap-2">
          <button onClick={()=>setShowImport(true)} className="tw-btn-outline inline-flex items-center gap-1 text-sm" data-testid="show-import-btn">
            <FileSpreadsheet size={14}/> Import from Excel
          </button>
          <button onClick={()=>setShowNew(s=>!s)} className="tw-btn-primary inline-flex items-center gap-1 text-sm" data-testid="show-new-course-btn">
            <Plus size={14}/> {showNew ? "Cancel" : "New course"}
          </button>
        </div>
      </div>
      {showNew && <div className="mt-4"><NewCourseForm onCreated={()=>{setShowNew(false); load();}} onCancel={()=>setShowNew(false)}/></div>}
      {showImport && <ImportWizard onClose={()=>setShowImport(false)} onImported={(r)=>{setShowImport(false); load(); setSelectedId(r.course_id);}}/>}

      <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="admin-course-grid">
        {courses.map(c => (
          <button key={c.course_id} onClick={()=>setSelectedId(c.course_id)} className={`tw-card p-4 text-left tw-card-hover ${selectedId===c.course_id?"ring-2 ring-stone-900":""}`} data-testid={`admin-course-${c.course_id}`}>
            <div className="h-1.5 w-10 rounded-full" style={{background:c.cover_color}}/>
            <div className="tw-mono-label mt-3">{c.course_id}</div>
            <div className="font-bold tw-display mt-1">{c.title}</div>
            <div className="text-xs text-stone-500 mt-1">{c.published ? "Published" : "Draft"}{c.author ? ` · ${c.author}` : ""}</div>
          </button>
        ))}
      </div>

      {selectedId && <CourseEditor key={selectedId} courseId={selectedId} refreshCourses={()=>{ load(); }}/>}
    </>
  );
}

/* ---------- AI Tools tab ---------- */
function AiToolsTab() {
  const [reading, setReading] = useState("");
  const [num, setNum] = useState(5);
  const [genResult, setGenResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const generate = async () => {
    setBusy(true); setErr(""); setGenResult(null);
    try {
      const { data } = await api.post("/ai/generate-quiz", { reading_text: reading, num_questions: Number(num) });
      setGenResult(data);
    } catch (e) { setErr(e.response?.data?.detail || "Generation failed"); }
    finally { setBusy(false); }
  };
  return (
    <section className="tw-card p-6">
      <div className="flex items-center gap-2 tw-mono-label" data-testid="admin-quiz-gen-label"><Sparkles size={14} className="text-amber-600"/> AI QUIZ GENERATOR (CLAUDE SONNET 4.5)</div>
      <h2 className="tw-display text-2xl font-bold mt-2">Generate questions from reading</h2>
      <p className="text-stone-600 text-sm mt-1">Standalone tool. To save questions to a specific lesson, open that lesson under Courses → Quiz.</p>
      <textarea value={reading} onChange={e=>setReading(e.target.value)} rows={6} placeholder="Paste reading content…" className="tw-input mt-4" data-testid="admin-quiz-reading-input"/>
      <div className="mt-3 flex items-center gap-3 flex-wrap">
        <label className="text-sm text-stone-600">Number of questions</label>
        <input type="number" min={1} max={15} value={num} onChange={e=>setNum(e.target.value)} className="tw-input w-24" data-testid="admin-quiz-num-input"/>
        <button onClick={generate} disabled={busy || !reading.trim()} className="tw-btn-primary inline-flex items-center gap-2" data-testid="admin-generate-quiz-btn">
          {busy ? <><Loader2 size={14} className="animate-spin"/> Generating…</> : <>Generate quiz</>}
        </button>
      </div>
      {err && <div className="text-red-600 text-sm mt-3" data-testid="admin-quiz-error">{err}</div>}
      {genResult && (
        <div className="mt-6 space-y-3" data-testid="admin-quiz-result">
          {genResult.questions?.map((q,i)=>(
            <div key={i} className="border border-stone-200 rounded-xl p-4">
              <div className="font-semibold">Q{i+1}. {q.q}</div>
              <ul className="mt-2 text-sm list-disc list-inside">
                {q.options.map((o,j)=>(<li key={j} className={j===q.correct?"font-bold text-emerald-700":""}>{o}{j===q.correct?" ✓":""}</li>))}
              </ul>
              {q.explanation && <div className="text-xs text-stone-500 mt-2 italic">{q.explanation}</div>}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export default function Admin() {
  const [tab, setTab] = useState("overview");
  return (
    <div className="min-h-screen">
      <TopNav/>
      <div className="max-w-7xl mx-auto px-6 py-10 fade-in" data-testid="admin-container">
        <div className="tw-mono-label">TAWBTI · ADMIN CONSOLE</div>
        <h1 className="tw-display text-4xl font-extrabold tracking-tight mt-1" data-testid="admin-title">Platform overview</h1>

        <div className="mt-6 border-b border-stone-200 flex gap-6">
          <TabBtn active={tab==="overview"} onClick={()=>setTab("overview")} testid="admin-tab-overview">Overview</TabBtn>
          <TabBtn active={tab==="courses"} onClick={()=>setTab("courses")} testid="admin-tab-courses">Course Builder</TabBtn>
          <TabBtn active={tab==="ai"} onClick={()=>setTab("ai")} testid="admin-tab-ai">AI Tools</TabBtn>
        </div>

        <div className="py-6">
          {tab === "overview" && <Overview/>}
          {tab === "courses" && <CoursesTab/>}
          {tab === "ai" && <AiToolsTab/>}
        </div>
      </div>
    </div>
  );
}
